import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import { useAuth } from './AuthContext';

const ALL_TOURS = ['welcome', 'welcome_nav', 'workouts', 'live_workout', 'diet', 'check_ins'];

const TutorialContext = createContext(null);

export function TutorialProvider({ children }) {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Fetch on mount and on user change. Triggers backend lazy-backfill once for
  // existing users (sets all-completed if field is missing).
  useEffect(() => {
    if (!user || user.role !== 'client') {
      setProgress(null);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get('/users/me/tutorial-progress');
        if (!cancelled) {
          setProgress(data.tutorial_progress);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) {
          // Failure: treat as all-completed so we never spam users on offline first load.
          const all = {};
          ALL_TOURS.forEach((id) => { all[id] = { state: 'completed', updated_at: null }; });
          setProgress(all);
          setLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  const setTourState = useCallback(async (tourId, state) => {
    if (!ALL_TOURS.includes(tourId)) return;
    setProgress((prev) => prev ? {
      ...prev,
      [tourId]: { state, updated_at: new Date().toISOString() }
    } : prev);
    try {
      const { data } = await client.patch('/users/me/tutorial-progress', { tour_id: tourId, state });
      setProgress(data.tutorial_progress);
    } catch {
      try {
        const { data } = await client.get('/users/me/tutorial-progress');
        setProgress(data.tutorial_progress);
      } catch { /* leave optimistic state */ }
    }
  }, []);

  const setBulk = useCallback(async (updates) => {
    setProgress((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const now = new Date().toISOString();
      updates.forEach(({ tour_id, state }) => {
        next[tour_id] = { state, updated_at: now };
      });
      return next;
    });
    try {
      const { data } = await client.patch('/users/me/tutorial-progress/bulk', { updates });
      setProgress(data.tutorial_progress);
    } catch { /* swallow; next refresh corrects */ }
  }, []);

  const tourState = useCallback((tourId) => progress?.[tourId]?.state || 'completed', [progress]);

  return (
    <TutorialContext.Provider value={{ progress, loaded, setTourState, setBulk, tourState }}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial must be used within TutorialProvider');
  return ctx;
}
